import {
  COMPONENT,
  COMPONENT_TYPE,
  URN as CONST_URN,
  DATA_PRODUCT,
  DMB,
  DOMAIN,
  GROUP,
  INFRASTRUCTURE_TEMPLATE,
  Optional,
  RELEASE,
  RESOURCE,
  SYSTEM_TYPE,
  TAXONOMY,
  USE_CASE_TEMPLATE,
  USER,
} from '@agilelab/plugin-wb-platform-common';
import {
  Entity,
  parseEntityRef,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import { ReleaseEntity } from '../types/model/ReleaseEntity';
import {
  ComponentTypeURN,
  ComponentURN,
  DomainURN,
  DPURN,
  GroupURN,
  ReleaseURN,
  ResourceURN,
  SubcomponentURN,
  SystemTypeURN,
  TaxonomyURN,
  URN,
  UseCaseTemplateURN,
  UserURN,
} from '../types/model/urns';

export function deconstructVersion(version: string): {
  major: number;
  minor: number | undefined;
  patch: number | undefined;
  isSnapshot: boolean;
  sequentialId: number | undefined;
} {
  try {
    const groups = RegExp(
      /^(\d+)(\*|.\d+)?(\*|.\d+)?(-SNAPSHOT)?.?(\*|\d+)?/,
    ).exec(version);

    return {
      major: Number(groups![1]),
      minor: groups![2] ? Number(groups![2].replace('.', '')) : undefined,
      patch: groups![3] ? Number(groups![3].replace('.', '')) : undefined,
      isSnapshot: !!groups![4],
      sequentialId: groups![5] ? Number(groups![5]) : undefined,
    };
  } catch (error) {
    throw new Error(
      `unable to parse version ${version}. The version should be in this format x.y[.z][-SNAPSHOT][-w], ${error}`,
    );
  }
}

/**
 * in releases/snapshots, the entity version is persisted without the patch number.
 * With this method, we force the patch number to send it to the coordinator that expects it.
 * @param options : pass either `v` or `release` to fetch the version from
 */
export function buildReleaseVersion(options: {
  release?: ReleaseEntity;
  v?: string;
}) {
  let version;
  const passedVersion = options.v ?? options.release?.metadata.version;
  if (!passedVersion)
    throw new Error(
      'You must pass either the release or the version as string',
    );
  const deconstructuredVersion = deconstructVersion(passedVersion);
  version = `${deconstructuredVersion.major}.${
    deconstructuredVersion.minor ?? 0
  }.${deconstructuredVersion.patch ?? 0}`;
  if (deconstructuredVersion.sequentialId)
    version += `-SNAPSHOT-${deconstructuredVersion.sequentialId}`;
  return version;
}

export function generateURNByKind(
  entityName: string,
  kind: string,
  verbatim: boolean = false,
): string {
  const urnKind = encodeKindForURN(kind);
  return [
    CONST_URN,
    DMB,
    urnKind,
    verbatim
      ? generateVerbatimURNNameForEntity(entityName, kind)
      : generateURNNameForEntity(entityName, kind),
  ].join(':');
}

export const entityRefToUrn = (ref: string) => {
  const entity = parseEntityRef(ref);
  const res = generateURNByKind(entity.name, entity.kind);

  return res;
};

export const entityToUrn = (entity: Entity) => {
  const res = generateURNByKind(entity.metadata.name, entity.kind);

  return res;
};

/**
 * DO NOT MODIFY THIS
 * Generates a URN starting from metadata.name and kind of an entity.
 * In contrast to what RBAC needs, this function performs an hyphen replacement. In fact, RBAC has its own generateURNByKind function.
 * However we cannot align those two generators otherwise any resource having an hyphen in its name would be duplicated in the catalog if we stop to do this replacement.
 * @param name - metadata.name inside the entity
 * @param kind
 * @returns
 */
export function generateURNNameForEntity(name: string, kind: string): string {
  if (kind?.toLocaleLowerCase() === 'domain') {
    return name
      .normalize('NFD')
      .replace(/[-]+/g, '') // we leave hyphen as the only char that we replace just for retrocompatibility
      .toLowerCase();
  }
  return name.replaceAll('.', ':');
}

/**
 * This is a variant of 'generateURNNameForEntity' function that leave hyphens in the name for the domain type.
 * @param name - metadata.name inside the entity
 * @param kind
 * @returns
 */
export function generateVerbatimURNNameForEntity(
  name: string,
  kind: string,
): string {
  if (kind?.toLocaleLowerCase() === 'domain') {
    return name.normalize('NFD').toLowerCase();
  }
  return name.replaceAll('.', ':');
}

export const INFRASTRUCTURE_TEMPLATE_NAME_REGEX = /^[a-zA-Z0-9-_]+$/;

export const INFRASTRUCTURE_TEMPLATE_VERSION_REGEX = /^(?:\d+|\d+\.\d+\.\d+)$/;

export function parseUrn(urn: string): Partial<URN> {
  const arr = urn.split(':');
  if (
    arr.length !== 4 &&
    arr.length !== 5 &&
    arr.length !== 6 &&
    arr.length !== 7 &&
    arr.length !== 8
  ) {
    throw new Error(`Malformed URN received: '${urn}'`);
  }

  const kind = arr[2].trim().toLowerCase();
  switch (kind) {
    case RELEASE:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        domain: arr[3],
        name: arr[4],
        version: arr[5],
        releaseVersion: arr[6],
      } as ReleaseURN;
    case DATA_PRODUCT:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        domain: arr[3],
        name: arr[4],
        version: arr[5],
      } as DPURN;
    case COMPONENT:
      if (arr.length !== 8)
        return {
          urn: CONST_URN,
          dmb: DMB,
          kind: arr[2],
          domain: arr[3],
          name: arr[4],
          version: arr[5],
          component: arr[6],
        } as ComponentURN;
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        domain: arr[3],
        name: arr[4],
        version: arr[5],
        component: arr[6],
        subcomponent: arr[7],
      } as SubcomponentURN;
    case RESOURCE:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        domain: arr.length > 4 ? arr[3] : undefined,
        name: arr[4] ?? arr[3],
      } as ResourceURN;
    case USER:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name: arr[3],
      } as UserURN;
    case GROUP:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name: arr[3],
      } as GroupURN;
    case DOMAIN:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        domain: arr[3],
      } as DomainURN;
    case SYSTEM_TYPE:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name: arr[3],
      } as SystemTypeURN;
    case COMPONENT_TYPE:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name: arr[3],
      } as ComponentTypeURN;
    case USE_CASE_TEMPLATE:
    case INFRASTRUCTURE_TEMPLATE: {
      const name = arr[3];
      const version = arr[4];

      if (!INFRASTRUCTURE_TEMPLATE_NAME_REGEX.test(name)) {
        throw new Error(
          `Invalid name: "${name}" in ${urn}. Must be alphanumeric, dashes (-), or underscores (_).`,
        );
      }

      if (!INFRASTRUCTURE_TEMPLATE_VERSION_REGEX.test(version)) {
        throw new Error(
          `Invalid version: "${version}" in ${urn}. Must be a positive integer or a dot-separated sequence of three numbers.`,
        );
      }

      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name,
        version,
      };
    }

    case TAXONOMY:
      return {
        urn: CONST_URN,
        dmb: DMB,
        kind: arr[2],
        name: arr[3],
      };

    default:
      throw Error(`Kind not recognized for URN '${urn}'`);
  }
}

export function encodeKindForURN(kind: string): string {
  if (kind?.trim() === '') {
    throw Error('Cannot encode kind to URN. Empty kind supplied');
  }

  const lowerCaseKind = kind?.trim()?.toLowerCase();
  switch (lowerCaseKind) {
    case 'system':
      return DATA_PRODUCT;
    case 'component':
      return COMPONENT;
    case 'resource':
      return RESOURCE;
    case 'domain':
      return DOMAIN;
    default:
      return lowerCaseKind;
  }
}

/**
 * Parses a given urn to the ID that is more familiar with the builder
 * @example component:default/marketing.asdf.0.impala-cdp-output-port
 * @param urn
 * @returns
 */
export const transformUrnToWitboostId = (urn: string) => {
  try {
    const entityUrn = parseUrn(urn);
    const entityKind = entityUrn?.kind?.trim().toLowerCase();
    if (entityUrn)
      switch (entityKind) {
        case COMPONENT: {
          const componentUrn = entityUrn as ComponentURN | SubcomponentURN;
          const subcomponent = (
            componentUrn as Optional<SubcomponentURN, 'subcomponent'>
          ).subcomponent;
          return stringifyEntityRef({
            name: `${componentUrn.domain}.${componentUrn.name}.${
              componentUrn.version
            }.${componentUrn.component}${
              subcomponent ? `.${subcomponent}` : ''
            }`,
            kind: 'component',
          });
        }
        case DOMAIN: {
          const domainUrn = entityUrn as DomainURN;
          return stringifyEntityRef({
            name: `${domainUrn.domain}`,
            kind: 'domain',
          });
        }
        case USER: {
          const userUrn = entityUrn as UserURN;
          return stringifyEntityRef({ name: `${userUrn.name}`, kind: 'user' });
        }
        case GROUP: {
          const groupUrn = entityUrn as GroupURN;
          return stringifyEntityRef({
            name: `${groupUrn.name}`,
            kind: 'group',
          });
        }
        case DATA_PRODUCT: {
          const dpUrn = entityUrn as DPURN;
          return stringifyEntityRef({
            name: `${dpUrn.domain}.${dpUrn.name}.${dpUrn.version}`,
            kind: 'system',
          });
        }
        case RESOURCE: {
          const rsrUrn = entityUrn as ResourceURN;
          return stringifyEntityRef({
            name: `${rsrUrn.domain ? `${rsrUrn.domain}.` : ''}${rsrUrn.name}`,
            kind: 'resource',
          });
        }
        case USE_CASE_TEMPLATE: {
          const utmUrn = entityUrn as UseCaseTemplateURN;
          return stringifyEntityRef({
            name: utmUrn.name,
            kind: 'template',
          });
        }
        case RELEASE: {
          const releaseUrn = entityUrn as ReleaseURN;
          return stringifyEntityRef({
            name: `${releaseUrn.domain}.${releaseUrn.name}.${releaseUrn.version}.${releaseUrn.releaseVersion}`,
            kind: 'release',
          });
        }
        case TAXONOMY: {
          const taxonomyUrn = entityUrn as TaxonomyURN;
          return stringifyEntityRef({
            name: `${taxonomyUrn.name}`,
            kind: 'taxonomy',
          });
        }
        case SYSTEM_TYPE: {
          const systemTypeUrn = entityUrn as SystemTypeURN;
          return stringifyEntityRef({
            name: `${systemTypeUrn.name}`,
            kind: 'systemtype',
          });
        }
        case COMPONENT_TYPE: {
          const componentTypeUrn = entityUrn as ComponentTypeURN;
          return stringifyEntityRef({
            name: `${componentTypeUrn.name}`,
            kind: 'componenttype',
          });
        }
        default:
          return undefined;
      }
  } catch (e) {
    return undefined;
  }
  return undefined;
};

export const normalizeInfrastructureTemplateName = (name: string) =>
  name
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/[^a-zA-Z0-9-_]/g, '') // Remove all other invalid characters;
    .toLowerCase(); // Convert uppercase letters to lowercase;

/**
 * Type mapping from URN kind to URN type
 */
type URNKindToType = {
  [DATA_PRODUCT]: DPURN;
  [DOMAIN]: DomainURN;
  [SYSTEM_TYPE]: SystemTypeURN;
  [COMPONENT]: ComponentURN;
  [COMPONENT_TYPE]: ComponentTypeURN;
};

/**
 * Parses and partitions URNs by their kind in a type-safe manner
 * @param urns - Array of URN strings to parse and partition
 * @param urnKinds - Array of URN kinds to filter by (e.g., [DOMAIN, DATA_PRODUCT])
 * @returns A type-safe object where each key is a URN kind and the value is an array of parsed URNs of that specific type
 * @example
 * ```ts
 * const urns = [
 *   'urn:dmb:cmp:domain1:name1:1:compA',
 *   'urn:dmb:dp:domain1:name1:1',
 *   'urn:dmb:dmn:domain3',
 * ];
 * const result = parseAndPartitionUrnsByKind(urns, [DOMAIN, DATA_PRODUCT]);
 * // result = {
 * //   dmn: [DomainURN],
 * //   dp: [DPURN]
 * // }
 * // TypeScript knows result.dmn is DomainURN[]
 * // TypeScript knows result.dp is DPURN[]
 * ```
 */
export function parseAndPartitionUrnsByKind<K extends keyof URNKindToType>(
  urns: string[],
  urnKinds: K[],
): Partial<{ [Key in K]: URNKindToType[Key][] }> {
  const result = {} as Partial<{ [Key in K]: URNKindToType[Key][] }>;
  const allowedKinds = new Set(urnKinds);

  for (const urn of urns) {
    try {
      const parsed = parseUrn(urn);
      const { kind } = parsed;

      if (!kind || !allowedKinds.has(kind as K)) continue;

      const typedKind = kind as K;

      if (!result[typedKind]) result[typedKind] = [];

      result[typedKind].push(parsed as URNKindToType[K]);
    } catch {
      // Skip malformed URNs
      continue;
    }
  }

  return result;
}
